import React from 'react';

const GamingService = () => {
  return (
    <section id="gaming-service" className="py-16 bg-gradient-to-br from-purple-600 to-indigo-800">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            DIGITAL SERVICES
          </h2>
          <p className="text-purple-100 text-lg max-w-3xl mx-auto">
            Seamless Mobile Game Monetization - Turn your games into revenue generators!
          </p>
        </div>

        {/* Gaming Service Card */}
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-2xl shadow-2xl overflow-hidden hover:transform hover:scale-105 transition-all duration-300">
            <div className="relative">
              <img 
                src="/services/service 9.png" 
                alt="Gaming Service Monetization" 
                className="w-full h-64 object-cover"
              />
              <div className="absolute top-4 left-4 bg-indigo-600 text-white px-3 py-1 rounded-full text-sm font-semibold flex items-center">
                <span className="animate-pulse mr-2">🎮</span> CARRIER-GRADE
              </div>
            </div>
            
            <div className="p-8">
              <h3 className="text-2xl font-bold text-gray-800 mb-3">
                Turn your games into revenue generators with carrier-grade subscription management!
              </h3>
              <p className="text-indigo-600 font-semibold mb-4 text-lg">
                Seamless mobile game monetization through direct carrier billing
              </p>
              
              <p className="text-gray-600 mb-6 leading-relaxed">
                Infinity Tech Africa's <strong>Gaming Service</strong> empowers game developers and publishers to 
                effortlessly monetize their mobile games through direct carrier billing. Our robust API handles everything 
                from user authentication to subscription renewals—letting you focus on creating amazing gaming experiences 
                while we handle the payments.
              </p>

              <div className="mb-6">
                <h4 className="font-bold text-gray-800 mb-3">Why Choose Our Gaming Service?</h4>
                <div className="space-y-2">
                  <div className="flex items-start">
                    <span className="text-indigo-500 mr-2 text-lg">✔</span>
                    <span className="text-gray-700"><strong>Carrier-Grade Billing</strong> - Direct mobile network integration with Safaricom for seamless payments.</span>
                  </div>
                  <div className="flex items-start">
                    <span className="text-indigo-500 mr-2 text-lg">✔</span>
                    <span className="text-gray-700"><strong>Flexible Subscriptions</strong> - Daily, weekly, or monthly billing cycles to match your game's needs.</span>
                  </div>
                  <div className="flex items-start">
                    <span className="text-indigo-500 mr-2 text-lg">✔</span>
                    <span className="text-gray-700"><strong>Zero Payment Friction</strong> - Users subscribe with just their phone number—no cards or wallets required.</span>
                  </div>
                  <div className="flex items-start">
                    <span className="text-indigo-500 mr-2 text-lg">✔</span>
                    <span className="text-gray-700"><strong>Real-Time Notifications</strong> - Instant payment confirmations and renewal alerts via webhooks.</span>
                  </div>
                  <div className="flex items-start">
                    <span className="text-indigo-500 mr-2 text-lg">✔</span>
                    <span className="text-gray-700"><strong>Developer-Friendly API</strong> - Clean REST endpoints with comprehensive documentation and support.</span>
                  </div>
                </div>
              </div>

              <div className="mb-6">
                <h4 className="font-bold text-gray-800 mb-3">Perfect for:</h4>
                <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg p-4 border border-indigo-100">
                  <div className="grid md:grid-cols-2 gap-3 text-gray-700">
                    <div className="flex items-start">
                      <span className="text-purple-600 mr-2">•</span>
                      <span>Mobile game monetization & premium features</span>
                    </div>
                    <div className="flex items-start">
                      <span className="text-purple-600 mr-2">•</span>
                      <span>Game subscriptions & VIP access tiers</span>
                    </div>
                    <div className="flex items-start">
                      <span className="text-purple-600 mr-2">•</span>
                      <span>In-game currency & virtual item sales</span>
                    </div>
                    <div className="flex items-start">
                      <span className="text-purple-600 mr-2">•</span>
                      <span>Gaming tournaments & exclusive content</span>
                    </div>
                    <div className="flex items-start">
                      <span className="text-purple-600 mr-2">•</span>
                      <span>Casual games & gaming platforms</span>
                    </div>
                  </div>
                </div>
              </div>

              

              <p className="text-indigo-600 font-semibold mb-6 text-center text-lg flex justify-center items-center">
                <span className="mr-2">Transform your games into sustainable revenue streams—effortlessly!</span>
                <span className="text-2xl">🎮</span>
              </p>

              <a
                href="/contact"
                className="inline-flex items-center justify-center w-full bg-gradient-to-r from-blue-600 to-green-600 text-white py-3 px-6 rounded-lg font-semibold hover:from-blue-700 hover:to-green-700 shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300 ease-in-out"
              >
                <span>Ready to Monetize Your Gaming Platform?</span>
                <svg 
                  className="w-5 h-5 ml-2 transform group-hover:translate-x-1 transition-transform duration-300" 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7l5 5m0 0l-5 5m5-5H6"></path>
                </svg>
              </a>
            </div>
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-16">
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 max-w-4xl mx-auto border border-white/20">
            <h3 className="text-2xl font-bold text-white mb-4">
              Ready to Revolutionize Your Game Monetization?
            </h3>
            <p className="text-purple-100 mb-6 text-lg">
              Join successful game developers who've transformed their revenue with our carrier-grade billing solutions
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/contact"
                className="inline-flex items-center justify-center bg-gradient-to-r from-blue-600 to-green-600 text-white px-8 py-3 rounded-lg font-semibold hover:from-blue-700 hover:to-green-700 shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300 ease-in-out"
              >
                Contact Our Team
              </a>
              {/* <a
                href="/contact"
                className="inline-flex items-center justify-center border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white hover:text-blue-600 shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300 ease-in-out"
              >
                Contact Us
              </a> */}
            </div>
            <p className="text-yellow-200 mt-6 text-sm">
              Powered by Infinity Tech Africa. Create. Transform.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default GamingService;